import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sparkles, Zap, TrendingUp, Crown, X } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [ctaVisible, setCtaVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    
    // Check if CTA was closed in this session
    const ctaClosed = sessionStorage.getItem('ctaClosed');
    if (ctaClosed === 'true') {
      setCtaVisible(false);
    }
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCloseCta = () => {
    setCtaVisible(false);
    sessionStorage.setItem('ctaClosed', 'true');
  };

  const previewCards = [
    {
      type: 'image',
      title: 'Cinematic Portrait Prompt',
      content: 'Portrait of a determined entrepreneur, golden hour lighting, shallow depth of field, Hasselblad camera, 85mm lens, f/1.4',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      category: 'Visual Studio',
      censored: false
    },
    {
      type: 'excel',
      title: 'Budget Tracker Formula',
      content: '=SUMIFS(Expenses!$D:$D, Expenses!$B:$B, A2, Expenses!$C:$C, ">="&DATE(2024,1,1))\n\n***[SUBSCRIBER EXCLUSIVE]***\n\nPivot Analysis: Monthly variance by category',
      category: 'Work & Productivity',
      censored: true
    },
    {
      type: 'resume',
      title: 'Senior Product Manager Resume',
      content: 'PROFESSIONAL SUMMARY\nResults-driven Product Manager with 8+ years leading cross-functional teams.\n\nKEY ACHIEVEMENTS\n***[SUBSCRIBER EXCLUSIVE]***',
      category: 'Business & Marketing',
      censored: true
    },
    {
      type: 'image',
      title: 'Abstract Tech Background',
      content: 'Futuristic circuit board pattern, neon blue and purple gradient, ***[SUBSCRIBER EXCLUSIVE]***',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=300&fit=crop',
      category: 'Visual Studio',
      censored: true
    },
    {
      type: 'social',
      title: 'Viral LinkedIn Post Structure',
      content: '[Hook]: "I made a mistake that cost my company £50K..."\n\n[Story]: ***[SUBSCRIBER EXCLUSIVE]***\n\n[Lesson]: Key takeaway in bold\n\n[CTA]: "What\'s your biggest business lesson?"',
      category: 'Content & Social Media',
      censored: true
    },
    {
      type: 'code',
      title: 'Python Data Analysis Script',
      content: 'import pandas as pd\nimport matplotlib.pyplot as plt\n\ndf = pd.read_csv("sales.csv")\nmonthly = df.groupby("month").sum()\nplt.plot(monthly.index, monthly.revenue)',
      category: 'Coding & Technical',
      censored: false
    },
    {
      type: 'image',
      title: 'Product Photography Prompt',
      content: 'Luxury watch on marble surface, soft studio lighting, reflections on surface, ***[SUBSCRIBER EXCLUSIVE]***',
      image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=300&fit=crop',
      category: 'Visual Studio',
      censored: true
    },
    {
      type: 'excel',
      title: 'Project Timeline Gantt',
      content: 'Task Name | Start | Duration | Dependencies\n──────────────────────────\nResearch  | Week 1 | 2 weeks | -\nDesign    | Week 2 | 3 weeks | Research\nDevelop   | Week 4 | 6 weeks | Design\n\nFORMULA: =NETWORKDAYS(B2,C2)',
      category: 'Work & Productivity',
      censored: false
    },
    {
      type: 'image',
      title: 'Minimalist Interior Design',
      content: 'Scandinavian living room, natural light, wooden floors, white walls, plants, cozy atmosphere, architectural photography',
      image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=400&h=300&fit=crop',
      category: 'Life & Personal Growth',
      censored: false
    },
    {
      type: 'social',
      title: 'TikTok Hook Formula',
      content: '"POV: You just discovered the productivity hack that saved me 10 hours per week..."\n\n***[SUBSCRIBER EXCLUSIVE]***\n[Call to action]',
      category: 'Content & Social Media',
      censored: true
    },
    {
      type: 'resume',
      title: 'Tech Lead Resume',
      content: 'TECHNICAL SKILLS\nLanguages: Python, TypeScript, Go, SQL\nFrameworks: React, FastAPI, Docker, Kubernetes\n\nLEADERSHIP\n***[SUBSCRIBER EXCLUSIVE]***',
      category: 'Coding & Technical',
      censored: true
    },
    {
      type: 'image',
      title: 'Food Photography Prompt',
      content: 'Gourmet burger on wooden board, steam rising, professional food photography, shallow depth of field, warm color grading, restaurant quality',
      image: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&h=300&fit=crop',
      category: 'Visual Studio',
      censored: false
    },
    {
      type: 'code',
      title: 'React Custom Hook',
      content: 'function useLocalStorage(key, initial) {\n  const [value, setValue] = useState(() => {\n    const stored = localStorage.getItem(key);\n    return stored ? JSON.parse(stored) : initial;\n  });\n  // ***[SUBSCRIBER EXCLUSIVE]***\n}',
      category: 'Coding & Technical',
      censored: true
    },
    {
      type: 'excel',
      title: 'Sales Dashboard KPIs',
      content: 'Revenue: =SUM(Sales!D:D)\nConversion Rate: ***[SUBSCRIBER EXCLUSIVE]***\nCAC: =Marketing_Spend/New_Customers\nLTV: =Avg_Order_Value*Purchase_Frequency*Customer_Lifespan',
      category: 'Business & Marketing',
      censored: true
    },
    {
      type: 'social',
      title: 'Instagram Reel Script',
      content: 'Scene 1: [Problem hook] "Struggling with...?"\nScene 2: [Show mistake] "Here\'s what NOT to do"\nScene 3: ***[SUBSCRIBER EXCLUSIVE]***\nScene 4: [Result] "This is what happens"\nScene 5: [CTA] "Save this for later!"',
      category: 'Content & Social Media',
      censored: true
    },
    {
      type: 'image',
      title: 'Abstract Art Prompt',
      content: 'Flowing liquid metal, abstract shapes, chrome reflections, vibrant colors, digital art, 8K resolution, trending on ArtStation',
      image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&h=300&fit=crop',
      category: 'Daily Trends (Featured)',
      censored: false
    }
  ];

  // Duplicate cards for infinite feel
  const allCards = [...previewCards, ...previewCards, ...previewCards];

  const getCardRotation = (index) => {
    const rotations = [-2.5, -1.5, 1, 2, -1, 1.5, -2, 0.5, -1.5, 2.5];
    return rotations[index % rotations.length];
  };

  return (
    <div className="relative min-h-screen overflow-hidden" style={{background: '#0a0a0a'}}>
      {/* Fixed Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{fontFamily: 'Outfit', color: '#f59e0b'}}>AIPedia</h1>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login-btn">
              Login
            </Button>
            <Button onClick={() => navigate('/register')} data-testid="nav-register-btn">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Newspaper Floor Hero Section */}
      <section className="relative pt-24 pb-32 px-4 md:px-8">
        {/* Ambient Glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[120px]"></div>
        </div>

        {/* Masonry Grid with Parallax */}
        <div 
          className="relative max-w-[1600px] mx-auto"
          style={{
            transform: `translateY(${scrollY * 0.1}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        >
          <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
            {allCards.map((card, index) => (
              <div
                key={index}
                className="break-inside-avoid mb-6 group cursor-pointer"
                style={{
                  transform: `rotate(${getCardRotation(index)}deg)`,
                  transition: 'all 0.3s ease'
                }}
                data-testid={`preview-card-${index}`}
                onClick={() => navigate('/register')}
              >
                <div 
                  className="relative bg-white/95 rounded-lg shadow-2xl overflow-hidden border border-gray-200"
                  style={{
                    transform: 'translateZ(0)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02) translateZ(0)';
                    e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(245, 158, 11, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateZ(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  {/* Category Badge */}
                  <div className="absolute top-3 right-3 z-10">
                    <span className="px-2 py-1 text-xs font-medium rounded" style={{background: '#f59e0b', color: '#000'}}>
                      {card.category}
                    </span>
                  </div>

                  {/* Censored Badge */}
                  {card.censored && (
                    <div className="absolute top-3 left-3 z-10">
                      <span className="px-2 py-1 text-xs font-bold rounded flex items-center gap-1" style={{background: '#ef4444', color: '#fff'}}>
                        <Crown className="w-3 h-3" />
                        LOCKED
                      </span>
                    </div>
                  )}

                  {/* Image Cards */}
                  {card.type === 'image' && card.image && (
                    <div className="relative h-64">
                      <img 
                        src={card.image} 
                        alt={card.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <h3 className="font-bold text-lg mb-2" style={{fontFamily: 'Outfit'}}>{card.title}</h3>
                        <p className="text-sm text-white/90 font-mono leading-relaxed">{card.content}</p>
                      </div>
                    </div>
                  )}

                  {/* Excel/Spreadsheet Cards */}
                  {card.type === 'excel' && (
                    <div className="p-6 bg-gradient-to-br from-green-50 to-white">
                      <h3 className="font-bold text-lg mb-3 text-gray-900" style={{fontFamily: 'Outfit'}}>{card.title}</h3>
                      <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap leading-relaxed bg-white/80 p-3 rounded border border-green-200">
                        {card.content}
                      </pre>
                    </div>
                  )}

                  {/* Resume Cards */}
                  {card.type === 'resume' && (
                    <div className="p-6 bg-gradient-to-br from-blue-50 to-white">
                      <h3 className="font-bold text-lg mb-3 text-gray-900" style={{fontFamily: 'Outfit'}}>{card.title}</h3>
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {card.content}
                      </pre>
                    </div>
                  )}

                  {/* Social Media Cards */}
                  {card.type === 'social' && (
                    <div className="p-6 bg-gradient-to-br from-purple-50 to-white">
                      <h3 className="font-bold text-lg mb-3 text-gray-900" style={{fontFamily: 'Outfit'}}>{card.title}</h3>
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {card.content}
                      </pre>
                    </div>
                  )}

                  {/* Code Cards */}
                  {card.type === 'code' && (
                    <div className="p-6 bg-gradient-to-br from-gray-900 to-gray-800">
                      <h3 className="font-bold text-lg mb-3 text-white" style={{fontFamily: 'Outfit'}}>{card.title}</h3>
                      <pre className="text-xs font-mono text-green-400 whitespace-pre-wrap leading-relaxed">
                        {card.content}
                      </pre>
                    </div>
                  )}

                  {/* Blur Overlay for Locked Content */}
                  <div className="absolute inset-0 backdrop-blur-[2px] bg-black/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-black/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-primary/50">
                      <p className="text-white text-sm font-medium flex items-center gap-2">
                        <Crown className="w-4 h-4" style={{color: '#f59e0b'}} />
                        Pro Only
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating CTA - Closable */}
        {ctaVisible && (
          <div className="fixed inset-0 pointer-events-none flex items-center justify-center z-40">
            <div className="pointer-events-auto relative text-center space-y-6 bg-black/90 backdrop-blur-xl rounded-2xl p-8 md:p-12 border-2" style={{borderColor: '#f59e0b', maxWidth: '600px', margin: '0 1rem'}}>
              {/* Close Button */}
              <button
                onClick={handleCloseCta}
                className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
                aria-label="Close"
                data-testid="close-cta-btn"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="space-y-4">
                <h2 className="text-4xl md:text-6xl font-bold" style={{fontFamily: 'Outfit', color: '#f59e0b'}}>
                  Unlock Everything
                </h2>
                <p className="text-xl md:text-2xl text-white font-semibold">
                  1,000+ Premium AI Prompts
                </p>
                <p className="text-muted-foreground text-lg">
                  Excel Templates • Image Prompts • Resume Builders<br />
                  Social Media Hooks • Code Snippets • Business Tools
                </p>
              </div>
              
              <div className="pt-4">
                <div className="text-5xl md:text-6xl font-bold mb-4" style={{color: '#f59e0b'}}>
                  £1.99<span className="text-2xl text-muted-foreground">/month</span>
                </div>
                <Button 
                  size="lg" 
                  className="w-full text-lg py-6 font-bold"
                  onClick={() => navigate('/register')}
                  data-testid="unlock-cta-btn"
                >
                  Unlock the Full Library →
                </Button>
                <p className="text-sm text-muted-foreground mt-3">Cancel anytime • New prompts daily</p>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Features Section */}
      <section className="relative py-24 px-4 md:px-8 bg-gradient-to-b from-transparent to-black/50">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-semibold tracking-tight mb-12 text-center" style={{fontFamily: 'Outfit'}}>
            Everything You Need, All in One Place
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-8 h-8" style={{color: '#f59e0b'}} />
              </div>
              <h4 className="text-xl font-semibold" style={{fontFamily: 'Outfit'}}>Daily Updates</h4>
              <p className="text-muted-foreground">
                Fresh prompts added every single day across all categories
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-8 h-8" style={{color: '#f59e0b'}} />
              </div>
              <h4 className="text-xl font-semibold" style={{fontFamily: 'Outfit'}}>7 Categories</h4>
              <p className="text-muted-foreground">
                Work, Visual Design, Social Media, Business, Personal Growth, Coding, Trends
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <Zap className="w-8 h-8" style={{color: '#f59e0b'}} />
              </div>
              <h4 className="text-xl font-semibold" style={{fontFamily: 'Outfit'}}>Instant Access</h4>
              <p className="text-muted-foreground">
                Search, filter, and copy any prompt in seconds
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center text-sm text-muted-foreground">
          <p>&copy; 2025 AIPedia. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
